// frontend/src/components/reports/ReportTable.tsx
//
// Tabla de los tres reports de cliente. Un solo componente parametrizado por
// `ReportDef`: nada específico de un report vive aquí.
//
// Cuatro decisiones de estructura que conviene conocer antes de tocar nada:
//
// 1. Hay DOS formas de pintar el detalle, y la elige el report con `nestedDetail`:
//
//    · **En la misma rejilla** (materia prima, producto terminado, cobertura). El
//      detalle comparte columnas con su cabecera — es el mismo report a otro nivel
//      — así que anidarlo rompería la alineación vertical, que es justo lo que
//      hace legible el informe.
//    · **Anidado en subtablas** (consumo de producción). Ahí los tipos de detalle
//      no comparten NI UNA columna con la cabecera ni entre sí: en una sola
//      rejilla hacían falta 35 columnas para que ninguna fila llenara más de
//      veinte, y el resto salían en blanco. Cada nivel lleva su propia cabecera
//      con sus columnas, y la maestra se queda con las del producto.
//
//    Lo anidado usa `SubTableWrapper` y `SubTotalsRow`, que son los mismos que
//    despliegan las líneas de un pedido de venta: el precio de sacar las columnas
//    del detalle de la rejilla maestra es que no se pueden congelar ni totalizar
//    en el pie de arriba, y por eso cada subtabla lleva su propio total.
//
// 2. El scroll vive en un contenedor de altura acotada, no en la página. Es lo que
//    da recorrido a la cabecera y al pie pegajosos: con la tabla creciendo
//    libremente, `sticky` no tiene por dónde desplazarse y el pie de totales acaba
//    fuera de la vista.
//
// 3. Hay una canaleta estrecha a la izquierda, fuera de las columnas del cliente,
//    con el marcador y el expansor, y nada más. El resumen del bloque plegado se
//    pinta como chips DENTRO de la primera columna congelada, junto al valor que
//    identifica la fila. Antes tenía canaleta propia de 136 a 184 px, y esa
//    canaleta iba dentro del bloque fijo: gastaba el espacio más caro de la tabla
//    en unos avisos que sólo salen cuando el bloque está plegado, y no pintaba
//    nada en las sublíneas, que son la mayoría de las filas.
//
// 4. Se congelan las columnas marcadas con `frozen`, que no tienen por qué ser las
//    primeras ni contiguas. Con 17 a 22 columnas y desplazamiento horizontal, sin
//    esto al llegar a la derecha no se sabe de qué fila se está leyendo.
import { Fragment, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { AggregationMenu } from '@/components/common/AggregationMenu'
import { SortableHeader } from '@/components/common/SortableHeader'
import {
  TableTotalsFooter,
  type TotalsFooterColumn,
  type TotalsFooterGroup,
} from '@/components/common/TableTotalsFooter'
import { useColumnAggregations } from '@/hooks/useColumnAggregations'
import { AGG_SYMBOL, computeAggregations, formatAggValue } from '@/utils/columnAggregations'
import type { AggColumn, AggFn, AnyReportRow } from '@/types'
import type { ReportBlock } from '@/utils/reportBlocks'
import { rowsOfType } from '@/utils/reportBlocks'
import { campo, claseEspecial, comoDic, DECIMALES_CANTIDAD, esVacio, fmtCell } from './formato'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { BarraMarcas, BotonMarca, CabeceraMarca, useMarcadas, useMarcasFilas } from '@/components/common/MarcasFilas'
import { SubTableWrapper } from '@/components/common/SubTableWrapper'
import { SubTotalsRow } from '@/components/common/SubTotalsRow'
import type { ColumnaPie } from '@/utils/subtotalesLineas'
import { claveBloqueReport, claveDetalleReport } from './marcasReport'
import { barraResaltada, fondoResaltado } from '@/components/pedidos-ventas/formato'
import {
  buildFooterColumns,
  columnaChips,
  frozenOffsets,
  GUTTER_W,
  stickyLabel,
  totalColumnCount,
} from './tableLayout'
import {
  colAppliesTo,
  colLabel,
  colsOfDetailType,
  colValueUi,
  isNumericCol,
  masterCols,
  PCT_DESVIO_RELEVANTE,
  sectionLabelUi,
  type ReportCol,
  type ReportDef,
  type ReportSummaryChip,
} from './reportDefs'

/** Solo recuentos en la columna de código: sumar códigos no significa nada. */
const COUNT_FNS: AggFn[] = ['count', 'countDistinct']


interface Props {
  def: ReportDef
  /**
   * Columnas visibles, en orden, de TODOS los niveles.
   *
   * Con detalle anidado, la tabla maestra pinta sólo las que valen en la fila de
   * cabecera y cada subtabla las suyas. Sin anidar, las pinta todas.
   */
  cols: ReportCol[]
  /** Bloques filtrados COMPLETOS: es sobre estos que se calculan los totales. */
  blocks: ReportBlock<AnyReportRow>[]
  /** Bloques de la página visible. */
  pageBlocks: ReportBlock<AnyReportRow>[]
  /** `tipoFila` de los niveles que se totalizan, uno por fila de pie. */
  aggLevels: string[]
  sortKey: string
  sortDir: 'asc' | 'desc'
  onSort: (key: string) => void
  /** Bloques desplegados. Lo posee la página, que también pinta «desplegar todo». */
  expanded: Set<number>
  onToggle: (key: number) => void
  /** ¿Es esta sublínea la que ha traído aquí a su bloque? Las que casan van
   *  resaltadas, igual que en la lista de pedidos de venta. `null` cuando no hay
   *  filtros de detalle activos: entonces no se resalta nada. */
  coincide?: ((row: AnyReportRow) => boolean) | null
  /**
   * ¿El pie cuenta sólo las sublíneas que casan?
   *
   * Un filtro de sublínea conserva el bloque ENTERO, así que sin esto el pie
   * totaliza sublíneas que no cumplen el filtro: buscando un componente salía la
   * suma de los 122 componentes de los productos que lo usan.
   */
  soloCoincidencias?: boolean
}

export function ReportTable({
  def, cols, blocks, pageBlocks, aggLevels, sortKey, sortDir, onSort, expanded, onToggle,
  coincide, soloCoincidencias = true,
}: Props) {
  const { t } = useTranslation()
  const dark = useAppStore(s => s.dark)
  // Los códigos de artículo se pintan según el rol: un usuario avp-only ve los
  // Armaf sin su letra de tipo. Va emparejado con el mismo formateo en el filtro
  // (`ReportFilters`); cambiar uno solo dejaría tabla y desplegable diciendo
  // cosas distintas del mismo artículo.
  const fmtArt = useFmtArticulo()

  // Marcas del usuario. El ámbito es el report, no la página: cada pestaña
  // analiza un conjunto distinto y mezclarlas no querría decir nada.
  //
  // Se lee el conjunto entero en vez de suscribir fila a fila porque aquí las
  // filas se pintan dentro de un `.map` y un hook por fila no cabe. Marcar
  // re-renderiza la página de la tabla, que son 50 bloques: asumible. En las
  // tablas sin paginar se usa `FilaMarcable`, que sólo repinta su fila.
  const marcas = useMarcasFilas(`report:${def.key}`)
  const marcadas = useMarcadas(marcas)
  // Las celdas congeladas llevan fondo propio para tapar lo que pasa por debajo,
  // así que la marca no puede pintarse sólo en el `<tr>`: se pasa como fondo de
  // fila y lo hereda también el bloque congelado.
  //
  // Son las mismas clases que usa `FilaMarcable`, no un par de utilidades
  // paralelas: el color de la marca se define UNA vez, en `index.css`.
  const marcadoBg = dark ? 'fila-marcada-dark' : 'fila-marcada'

  // ── Agregación ──────────────────────────────────────────────────────────────
  //
  // Acotada SIEMPRE a un único tipo de fila. En estos reports la cabecera ya es la
  // suma de su detalle, así que agregar los dos niveles a la vez duplicaría todo.
  // Lo decide el selector «Totales de», que es de opción única.
  /** Columnas agregables para un nivel. Cambian con él por la deduplicación. */
  const colsDeNivel = useCallback((nivel: string): AggColumn<AnyReportRow>[] => {
    const out: AggColumn<AnyReportRow>[] = def.cols
      .filter(isNumericCol)
      .map(c => ({
        key: c.key,
        numeric: true,
        decimals: c.decimals ?? DECIMALES_CANTIDAD,
        defaultAgg: c.defaultAgg,
        // La deduplicación solo aplica al nivel de detalle: es ahí donde la misma
        // entidad se repite bajo varias cabeceras.
        dedupeKey: def.dedupeBy && nivel !== def.headerType ? def.dedupeBy : undefined,
      }))
    if (def.distinctField) {
      out.push({ key: def.distinctField, distinctKey: def.distinctField })
    }
    return out
  }, [def])

  // El estado de las Σ es del REPORT y no del nivel: «suma» es suma tanto en la
  // cabecera como en el detalle, y con varios niveles a la vez no puede haber una
  // elección por nivel. El hook sólo se usa para poseer ese estado — los totales
  // se calculan grupo a grupo más abajo.
  /**
   * Filas de un nivel que entran en el total.
   *
   * En los niveles de DETALLE, con el modo «sólo coincidencias» se quedan las que
   * cumplen el filtro de sublínea — el mismo predicado que las pinta resaltadas,
   * así que lo que se suma es exactamente lo que se ve en azul.
   *
   * El nivel de CABECERA no se filtra: los bloques ya vienen filtrados, así que
   * sus cabeceras son las mismas en los dos modos.
   */
  const filasDeNivel = useCallback((nivel: string) => {
    const todas = rowsOfType(blocks, nivel)
    if (!soloCoincidencias || !coincide || nivel === def.headerType) return todas
    return todas.filter(coincide)
  }, [blocks, soloCoincidencias, coincide, def])

  const primerNivel = aggLevels[0] ?? def.headerType
  const aggRows = useMemo(() => filasDeNivel(primerNivel), [filasDeNivel, primerNivel])
  const aggCols = useMemo(() => colsDeNivel(primerNivel), [colsDeNivel, primerNivel])

  const { aggState, setAgg, results } = useColumnAggregations({
    // `.v4` descarta la guardada antes de que existieran la merma y las tres
    // columnas del granel completo: en la franja de totales salían ausentes,
    // porque `loadState` se queda sólo con lo guardado cuando hay algo guardado y
    // una columna nueva nunca estrena su `defaultAgg`.
    //
    // `.v3` descartó a su vez la que iba con clave por nivel: al poder totalizar
    // varios niveles a la vez, la elección de Σ ya no puede ser una por nivel. Es
    // una preferencia menor y vuelve a sus valores por defecto.
    //
    // `.v2` descartó a su vez la de agosto de 2026, al entrar las columnas de la
    // cuenta del faltante.
    //
    // `loadState` se queda SOLO con lo guardado cuando hay algo guardado, así que
    // una columna nueva no estrena su `defaultAgg` en un navegador que ya había
    // usado la pantalla: el pie salía sin el total del FALTANTE justo a quien
    // lleva meses usando el report. Distinguir «nunca la vio» de «la apagó él»
    // pide cambiar el formato persistido, y ese hook lo comparten seis tablas.
    // Cambiar la clave resuelve el caso de hoy sin tocar a las demás; el precio
    // es que se pierden las elecciones de Σ propias de reports, que son una
    // preferencia menor y vuelven a sus valores por defecto.
    storageKey: `${def.storageKey}.v4`,
    rows: aggRows,
    columns: aggCols,
  })

  /** Con deduplicación activa, cuántas entidades distintas hay en unas filas. */
  const etiquetaDedup = useCallback((nivel: string, filas: AnyReportRow[]) => {
    if (!def.dedupeBy || nivel === def.headerType) return undefined
    const key = def.dedupeBy
    const distintos = new Set(
      filas.map(r => campo(r, key)).filter(v => v != null && v !== ''),
    ).size
    return t('reports.dedupedEntities', {
      count: distintos,
      formattedCount: distintos.toLocaleString('es-ES'),
    })
  }, [def, t])

  /**
   * Filas del pie: una por NIVEL elegido, y dentro de cada nivel una por SECCIÓN.
   *
   * Los dos ejes existen por razones distintas y las dos siguen valiendo:
   *
   * · Los niveles no se pueden sumar entre sí porque la cabecera YA es la suma de
   *   su detalle: sumar los dos en la misma celda contaría todo dos veces. En
   *   filas separadas no se duplica nada, y ver a la vez los litros de los lotes y
   *   los kilos de los componentes es justo lo que se le pide al selector.
   *
   * · Las secciones no se pueden sumar entre sí porque no son la misma magnitud.
   *   Corrige un cuadre que el cliente usa para validar el informe entero: sumando
   *   terminados y semielaborados juntos, `TOTAL QTY` menos sus cuatro componentes
   *   daba −71.717 en lugar de 0, porque el reservado de un granel está en litros o
   *   botellas montadas y el de un terminado en unidades de perfume.
   *
   * Con un solo nivel y sin secciones sale UNA fila y sin rótulo, que es como se
   * ha visto siempre: el rótulo sólo aparece cuando hay algo que distinguir.
   */
  const footerGroups = useMemo<TotalsFooterGroup[] | undefined>(() => {
    const campoSeccion = def.sectionField
    const nivelUnico = aggLevels.length <= 1

    const grupos: TotalsFooterGroup[] = []
    for (const nivel of aggLevels) {
      const filasNivel = filasDeNivel(nivel)
      // No `cols`: taparía el prop del mismo nombre, que es el que tiene los
      // rótulos y los niveles de cada columna.
      const aggsNivel = colsDeNivel(nivel)
      // El modo va en el rótulo: un total de «Componentes» que cuenta 12 de 122
      // tiene que decirlo, o se lee como el total del informe.
      const filtrado = soloCoincidencias && !!coincide && nivel !== def.headerType
      const nombreNivel = t(
        def.levelOptions.find(o => o.tipoFila === nivel)?.labelKey ?? nivel,
      ) + (filtrado ? ` · ${t('reports.filters.totalizeMatches').toLowerCase()}` : '')

      // Secciones presentes EN ESTE NIVEL. Un nivel de detalle puede no traer el
      // campo de sección, y entonces va en una sola fila.
      const secciones: string[] = []
      if (campoSeccion) {
        for (const r of filasNivel) {
          const v = String(campo(r, campoSeccion) ?? '')
          if (v && !secciones.includes(v)) secciones.push(v)
        }
      }

      // Con el detalle anidado, las columnas de un nivel de detalle no están en
      // la rejilla maestra: su total va como franja rotulada. El de cabecera sí
      // encaja en la rejilla y se pinta como siempre.
      if (def.nestedDetail && nivel !== def.headerType) {
        const propias = colsOfDetailType(cols, nivel).filter(isNumericCol)
        const res = computeAggregations(filasNivel, aggsNivel, aggState)
        const inline = propias
          .filter(c => res[c.key] && res[c.key].kind === 'number' && res[c.key].count > 0)
          .map(c => {
            const r = res[c.key]
            // El símbolo de la operación delante, igual que en la rejilla: sin
            // él, la media de 122 componentes se lee como el total del informe.
            const prefijo = r.fn === 'sum' ? '' : `${AGG_SYMBOL[r.fn]} `
            return {
              label: colLabel(c, t),
              value: r.kind === 'number'
                ? prefijo + formatAggValue(r.value, r.fn, c.decimals ?? DECIMALES_CANTIDAD)
                : '—',
            }
          })
        // Sin ninguna cifra que enseñar no se pinta la franja: mejor nada que un
        // recuento suelto, que es exactamente lo que había que arreglar.
        if (inline.length > 0) {
          grupos.push({
            label: nombreNivel,
            results: {},
            rowCount: filasNivel.length,
            rowLabel: etiquetaDedup(nivel, filasNivel),
            inline,
          })
        }
        continue
      }

      if (secciones.length === 0) {
        grupos.push({
          label: nivelUnico ? '' : nombreNivel,
          results: computeAggregations(filasNivel, aggsNivel, aggState),
          rowCount: filasNivel.length,
          rowLabel: etiquetaDedup(nivel, filasNivel),
        })
        continue
      }

      for (const sec of secciones) {
        const filas = filasNivel.filter(r => String(campo(r, campoSeccion!) ?? '') === sec)
        const seccion = sectionLabelUi(def, sec, t)
        grupos.push({
          // Con un solo nivel el rótulo es la sección a secas, como siempre; con
          // varios se antepone el nivel, porque «SEMI-FINISHED» tres veces no
          // diría de cuál de los tres niveles es cada fila.
          label: nivelUnico ? seccion : `${nombreNivel} · ${seccion}`,
          results: computeAggregations(filas, aggsNivel, aggState),
          rowCount: filas.length,
          rowLabel: etiquetaDedup(nivel, filas),
        })
      }
    }
    return grupos.length > 0 ? grupos : undefined
  }, [def, blocks, aggLevels, colsDeNivel, aggState, etiquetaDedup, filasDeNivel,
      soloCoincidencias, coincide, t])

  // ── Columnas congeladas ─────────────────────────────────────────────────────
  //
  // Geometría en `tableLayout`, en funciones puras, para poder fijarla con un test:
  // cabecera, cuerpo y pie tienen que emitir la misma rejilla.
  //
  // Con detalle anidado la rejilla maestra son SÓLO las columnas del producto: las
  // de cada nivel de detalle se pintan dentro de su subtabla. La cabecera, el pie,
  // las congeladas y el `colSpan` de las bandas se miden todos sobre ella, y por
  // eso se calcula una vez aquí y no en cada sitio.
  const colsTabla = useMemo(() => masterCols(def, cols), [def, cols])

  const offsets = useMemo(() => frozenOffsets(colsTabla), [colsTabla])
  const etiquetaPie = useMemo(() => stickyLabel(colsTabla), [colsTabla])

  /** Columna que aloja los chips del resumen: la primera congelada. */
  const claveChips = useMemo(() => columnaChips(colsTabla), [colsTabla])

  const footerCols: TotalsFooterColumn[] = useMemo(
    () => buildFooterColumns(colsTabla), [colsTabla],
  )

  // ── Estilos ─────────────────────────────────────────────────────────────────
  //
  // Fondos OPACOS y una sola clase de fondo por fila: la celda congelada tiene que
  // pintar el MISMO color que su fila, y con fondos translúcidos el color que se ve
  // depende de lo que haya debajo. Además, apilar dos clases de fondo en el mismo
  // `<tr>` deja que gane la que el CSS ponga después.
  // El padding va aparte del resto del estilo porque la canaleta lleva el suyo:
  // no es una columna de dato y no tiene por qué pagar la sangría de una.
  // El `nowrap` es lo que obliga a cada columna a medir su rótulo entero. Donde el
  // report lo permite, el rótulo parte en dos líneas y la columna mide su dato.
  const thWrap = def.wrapHeaders ? 'whitespace-normal leading-tight' : 'whitespace-nowrap'
  const thTipo = `py-2 text-[11px] font-bold uppercase tracking-wider ${thWrap} ${
    dark ? 'text-slate-400' : 'text-slate-500'
  }`
  const thBase = `px-3 ${thTipo}`
  const thBg = dark ? 'bg-slate-900' : 'bg-white'
  const rowBorder = dark ? 'border-t border-white/10' : 'border-t border-slate-200'
  const detailBorder = dark ? 'border-t border-white/5' : 'border-t border-slate-100'
  const headerBg = dark ? 'bg-slate-800' : 'bg-slate-100'
  const headerHover = dark ? 'hover:bg-slate-700' : 'hover:bg-slate-200'
  const detailBg = dark ? 'bg-slate-900' : 'bg-white'
  const detailHover = dark ? 'hover:bg-slate-800' : 'hover:bg-slate-50'
  const limitanteBg = dark ? 'bg-amber-950/40' : 'bg-amber-50'
  const limitanteHover = dark ? 'hover:bg-amber-950/60' : 'hover:bg-amber-100'
  // La sublínea que explica por qué está aquí el bloque. Mismas clases que la
  // lista de pedidos de venta, importadas de un sitio: es el mismo gesto y tiene
  // que verse igual. Manda sobre el ámbar del limitante —el ámbar dice «este
  // componente frena», el azul dice «esto es lo que buscabas»— y cede ante la
  // marca del usuario, que es una anotación suya y gana siempre.
  const coincideBg = fondoResaltado(dark)
  const coincideHover = dark ? 'hover:bg-sky-500/[0.18]' : 'hover:bg-sky-100'
  const coincideBarra = barraResaltada(dark)
  const tdTipo = `py-1.5 text-[12px] whitespace-nowrap ${
    dark ? 'text-slate-100' : 'text-slate-700'
  }`
  const tdBase = `px-3 ${tdTipo}`
  const tdCanaleta = `px-2 ${tdTipo}`
  const muted = dark ? 'text-slate-500' : 'text-slate-400'
  // Subtablas del detalle anidado. Tipografía más pequeña que la maestra a
  // propósito: dice «esto cuelga de la fila de arriba» sin necesidad de sangrar
  // más, que es espacio que en un report de treinta columnas no sobra.
  const subBorde = dark ? 'border-white/10' : 'border-slate-200'
  const subTh = `px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${thWrap} ${muted}`
  const subTd = `px-2 py-1 text-[11.5px] whitespace-nowrap ${dark ? 'text-slate-200' : 'text-slate-700'}`
  const subHover = dark ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-100/70'

  const totalCols = totalColumnCount(colsTabla)

  /**
   * Ancho útil de una celda: su ancho declarado menos la sangría de los dos
   * lados. La maestra va a `px-3` y las subtablas a `px-2`, 8 px menos.
   */
  function anchoContenido(col: ReportCol, sangria = 24): number {
    return (col.minW ?? 120) - sangria
  }

  /**
   * ¿Hay que recortar este texto?
   *
   * Con reparto FIJO la celda no crece con su contenido, pero tampoco lo sujeta:
   * el texto se sale, y el contenido de una celda se pinta DESPUÉS de los fondos
   * de todas las demás, así que las últimas letras acaban escritas encima de la
   * columna siguiente. Pasaba con «ARMAF CDN INTENSE OVERDOSE "JA"100ML EDP»,
   * que sobra 72 px de su columna y se metía en las cifras de PIEZAS PRODUCIDAS.
   *
   * Sólo el texto: una cifra recortada sería un dato falso, y para eso están los
   * anchos declarados, que se calculan para el número más largo.
   */
  function recorta(col: ReportCol, texto: string): boolean {
    return !!def.wrapHeaders && texto !== '' && !isNumericCol(col) && col.kind !== 'flag'
  }

  /**
   * ¿Merece la pena el tooltip con el texto entero?
   *
   * Sólo si el texto no cabe: en un código de artículo el tooltip repetiría lo
   * que ya se lee. 6,5 px por letra es una cota por debajo de lo medido (12 px
   * normal ronda 6,9), para equivocarse hacia enseñarlo.
   */
  function noCabe(col: ReportCol, texto: string, sangria = 24): boolean {
    return texto.length * 6.5 > anchoContenido(col, sangria)
  }

  /**
   * Ancho de una columna. Con rótulos de dos líneas se fija de verdad.
   *
   * `minWidth` a secas no basta: en `table-layout: auto` el navegador reparte por
   * contenido máximo —el rótulo entero en una línea— y se salta el mínimo por
   * arriba. Con `width` sí parte el rótulo y respeta la cifra.
   */
  function ancho(col: ReportCol) {
    return def.wrapHeaders
      ? { width: col.minW, minWidth: col.minW }
      : { minWidth: col.minW }
  }

  /** Distintivo de una celda de marca (`kind: 'flag'`). */
  function marca(col: ReportCol, texto: string) {
    const alerta = dark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'
    const neutro = dark ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-500'
    return (
      <span
        className={`px-1.5 py-0.5 rounded text-[10px] font-bold not-italic whitespace-nowrap ${
          col.flagTone === 'alert' ? alerta : neutro
        }`}
      >
        {texto}
      </span>
    )
  }

  /**
   * Rótulo de un tipo de detalle, para la banda que los separa dentro del bloque.
   *
   * Reutiliza los rótulos del selector «Totales de» (`levelOptions`) en lugar de
   * pedir claves nuevas: son exactamente los mismos nombres —«Lotes de granel»,
   * «Componentes»— y tenerlos en dos sitios acabaría con uno de los dos desfasado.
   */
  function rotuloDetalle(tipoFila: unknown): string {
    const opcion = def.levelOptions.find(o => o.tipoFila === String(tipoFila))
    return opcion ? t(opcion.labelKey) : String(tipoFila ?? '')
  }

  /** Texto ya traducido de un dato del resumen. */
  function textoChip(c: ReportSummaryChip) {
    return t(c.labelKey, { count: c.value, formattedValue: c.value.toLocaleString('es-ES') })
  }

  /**
   * Resumen del bloque plegado, dentro de la primera columna congelada.
   *
   * Todos los chips conservan su texto. Cuando el resumen vivía en su propia
   * canaleta, el RECUENTO se pintaba como número desnudo bajo un rótulo de
   * cabecera («LOTES», «COMP.») que decía una vez lo que se estaba contando; aquí
   * la cabecera es la de la columna que los aloja y ya nombra otra cosa, así que
   * un «13» suelto junto a un código de artículo no se sabría de qué es.
   *
   * Si no caben, recortan por la cola y el `title` de la celda lleva el texto
   * entero. Por eso los AVISOS van primero y el recuento al final: lo que se
   * recorta tiene que ser lo que menos pide una acción. El recuento dice cuántas
   * sublíneas hay —«13 componentes» en las cincuenta filas—; el aviso dice que
   * ese terminado no se puede fabricar.
   */
  function resumen(chips: ReportSummaryChip[]) {
    if (chips.length === 0) return null
    const tono = {
      neutral: dark ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-500',
      alert: dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700',
      danger: dark ? 'bg-red-500/15 text-red-300' : 'bg-red-100 text-red-700',
    }
    const porImportancia = [
      ...chips.filter(c => c.tone && c.tone !== 'neutral'),
      ...chips.filter(c => !c.tone || c.tone === 'neutral'),
    ]
    return (
      <>
        {porImportancia.map(c => (
          <span
            key={c.labelKey}
            className={`px-1 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${
              tono[c.tone ?? 'neutral']
            }`}
          >
            {textoChip(c)}
          </span>
        ))}
      </>
    )
  }

  /** El mismo resumen en texto plano, para el `title` cuando los chips se recortan. */
  function textoResumen(chips: ReportSummaryChip[]) {
    return chips.map(textoChip).join(' · ')
  }

  /**
   * Color de una celda de desvío: rojo si se consumió MÁS de lo previsto, verde si
   * menos, y nada mientras la diferencia sea ruido de redondeo.
   *
   * Es el único color que lleva un dato en toda la tabla. Con 30 columnas de
   * números iguales, el informe no enseñaba de un vistazo lo único que existe
   * para enseñar; y el umbral es el mismo que decide el chip del bloque plegado,
   * así que la fila y su resumen nunca dicen cosas distintas.
   */
  function colorDesvio(row: AnyReportRow, col: ReportCol): string {
    if (col.tone !== 'deviation') return ''
    // Sustitución de material: el porcentaje sigue siendo cierto pero no significa
    // lo que el color diría.
    if (col.toneSuppressedBy && !esVacio(campo(row, col.toneSuppressedBy))) return ''
    const v = Number(campo(row, col.key))
    if (!Number.isFinite(v) || Math.abs(v) <= PCT_DESVIO_RELEVANTE) return ''
    return v > 0
      ? `font-semibold ${dark ? 'text-rose-300' : 'text-rose-600'}`
      : `font-semibold ${dark ? 'text-emerald-300' : 'text-emerald-600'}`
  }

  /** Celda de una subtabla: sin congelar, sin chips y sin canaleta. */
  function celdaSub(row: AnyReportRow, col: ReportCol) {
    const texto = colValueUi(col, fmtCell(row, col, fmtArt), t)
    const color = colorDesvio(row, col)
    const pintaMarca = col.kind === 'flag' && texto !== ''
    return (
      <td
        key={col.key}
        className={`${subTd} ${isNumericCol(col) ? 'text-right tabular-nums' : 'text-left'}`}
        style={ancho(col)}
        title={recorta(col, texto) && noCabe(col, texto, 16) ? texto : undefined}
      >
        {pintaMarca ? marca(col, texto) : color ? <span className={color}>{texto}</span>
          : recorta(col, texto) ? (
            <span
              className="block overflow-hidden text-ellipsis"
              style={{ width: anchoContenido(col, 16) }}
            >
              {texto}
            </span>
          ) : texto}
      </td>
    )
  }

  /**
   * Columnas del pie de una subtabla, en el mismo orden que su cabecera.
   *
   * Sólo suman las ADITIVAS, que es el criterio del pie de las líneas de un
   * pedido: sumar un porcentaje de desvío o una densidad daría una cifra con
   * pinta de buena. La primera entrada es el hueco de la columna del marcador,
   * para que la rejilla del pie cuadre con la de la cabecera.
   */
  function pieDeSubtabla(colsNivel: ReportCol[]): ColumnaPie<AnyReportRow>[] {
    return [
      { key: '__marca' },
      ...colsNivel.map(col => ({
        key: col.key,
        agg: col.defaultAgg === 'sum'
          ? { key: col.key, numeric: true, decimals: col.decimals ?? DECIMALES_CANTIDAD }
          : undefined,
      })),
    ]
  }

  /**
   * Celda de datos, respetando el nivel al que aplica cada columna.
   *
   * `chips` sólo llega en la celda que aloja el resumen del bloque: el valor se
   * queda a la izquierda con su ancho, y los chips ocupan lo que sobra y recortan
   * si no caben.
   */
  function celda(
    row: AnyReportRow,
    col: ReportCol,
    isHeader: boolean,
    ci: number,
    filaBg: string,
    chips?: ReportSummaryChip[],
  ) {
    // El tipo de fila afina el nivel: con dos tipos de detalle, una columna de
    // lotes de granel no aplica a una fila de componente aunque las dos sean
    // detalle.
    const aplica = colAppliesTo(col, isHeader, String(row.tipoFila ?? ''))
    // Vacío, no guion, cuando la columna no aplica a este nivel: el guion queda
    // reservado para el dato que sí aplica y viene nulo, que es la distinción que
    // el cliente tiene que poder leer.
    // `colValueUi` traduce los valores de enum al idioma de la interfaz. El Excel
    // no pasa por aquí: allí van en inglés, como todo el contenido del fichero.
    const texto = aplica ? colValueUi(col, fmtCell(row, col, fmtArt), t) : ''
    const pintaMarca = col.kind === 'flag' && texto !== ''
    const left = offsets[ci]
    const fija = left != null
    const conChips = chips != null && chips.length > 0
    const color = colorDesvio(row, col)
    return (
      <td
        key={col.key}
        className={`${tdBase} ${isNumericCol(col) ? 'text-right tabular-nums' : 'text-left'} ${
          isHeader ? 'font-semibold' : ''
        } ${fija ? `${filaBg} sticky z-10` : ''}`}
        style={{ ...ancho(col), ...(fija ? { left } : {}) }}
        title={
          conChips ? textoResumen(chips)
          : recorta(col, texto) && noCabe(col, texto) ? texto
          : undefined
        }
      >
        {conChips ? (
          // Ancho FIJO, no un máximo: una celda de tabla crece con su contenido y
          // `overflow` no la sujeta, así que sin esto la columna se ensanchaba por
          // encima de su `minW` —328 px medidos donde declara 250— y la siguiente
          // congelada, que se ancla con el ancho DECLARADO, se le montaba encima
          // al desplazar. Con un hijo de ancho fijo, la columna mide lo que dice.
          <span
            className="flex items-center gap-1.5 overflow-hidden"
            style={{ width: anchoContenido(col) }}
          >
            <span className="shrink-0">{texto}</span>
            {resumen(chips)}
          </span>
        ) : pintaMarca ? marca(col, texto) : color ? (
          // El color va en un HIJO y no en la clase de la celda: `text-rose-600` y
          // el `text-slate-700` de la celda tienen la misma especificidad, así que
          // ahí ganaba el orden del CSS —el gris— y el desvío salía sin pintar.
          // Heredado por un hijo, gana el hijo y no depende de cómo se ordene la
          // hoja de estilos.
          <span className={color}>{texto}</span>
        ) : recorta(col, texto) ? (
          // Ancho FIJO en un hijo, igual que en las celdas con chips: `overflow`
          // sobre la celda no la sujeta, porque una celda de tabla mide su
          // contenido. El nombre entero se lee en el tooltip.
          <span
            className="block overflow-hidden text-ellipsis"
            style={{ width: anchoContenido(col) }}
          >
            {texto}
          </span>
        ) : texto}
      </td>
    )
  }

  return (
    <>
      {/* Franja de recuento, fuera del contenedor con scroll para que no se
          vaya de la vista al desplazar la tabla. */}
      <BarraMarcas dark={dark} marcas={marcas} className="px-3 pb-2" />

      <div className="overflow-auto max-h-[calc(100vh-21rem)] min-h-[16rem]">
      {/* `table-fixed` donde el report declara sus anchos: sin él, el reparto
          automático da a cada columna el ancho de su RÓTULO en una línea y se
          salta la cifra declarada por arriba. Con reparto fijo la tabla mide la
          suma de sus columnas y el rótulo parte en dos líneas. */}
      <table className={`w-full border-collapse ${def.wrapHeaders ? 'table-fixed' : ''}`}>
        <thead>
          <tr>
            {/* Canaleta: marcador y expansor, fuera de las columnas del cliente.
                Sin rótulo, porque ya no cuenta nada: el resumen del bloque vive en
                la primera columna congelada. */}
            <th
              className={`px-2 ${thTipo} ${thBg} sticky top-0 left-0 z-40`}
              style={{ width: GUTTER_W, minWidth: GUTTER_W }}
            />
            {colsTabla.map((col, ci) => {
              const numeric = isNumericCol(col)
              // `noAgg` desactiva el botón Σ en una columna numérica cuyo total
              // sería falso por venir repetida (ver `ReportCol.noAgg`).
              const aggregable = (numeric || col.key === def.distinctField) && !col.noAgg
              const left = offsets[ci]
              const fija = left != null
              // Rótulo de PANTALLA: sigue el toggle ES/EN de la barra lateral.
              // El de Excel es otro (`col.label`, el literal del cliente en inglés).
              const rotulo = colLabel(col, t)
              const label = col.tooltipKey
                ? (
                    <span title={t(col.tooltipKey)} className="cursor-help border-b border-dotted border-current/40">
                      {rotulo}
                    </span>
                  )
                : rotulo
              return (
                <SortableHeader
                  key={col.key}
                  dark={dark}
                  label={label}
                  sortKey={col.key}
                  activeSortKey={sortKey}
                  activeSortDir={sortDir}
                  onClick={() => onSort(col.key)}
                  align={numeric ? 'right' : 'left'}
                  wrap={def.wrapHeaders}
                  className={`${thBase} ${thBg} sticky top-0 ${fija ? 'z-40' : 'z-30'}`}
                  style={fija ? { left, ...ancho(col) } : ancho(col)}
                  extra={aggregable ? (
                    <AggregationMenu
                      dark={dark}
                      colLabel={rotulo}
                      active={aggState[col.key] ?? null}
                      onChange={fn => setAgg(col.key, fn)}
                      fns={numeric ? undefined : COUNT_FNS}
                    />
                  ) : undefined}
                />
              )
            })}
          </tr>
        </thead>

        <tbody>
          {pageBlocks.map((block, i) => {
            const open = expanded.has(block.key)
            const prev = pageBlocks[i - 1]
            const seccion = def.sectionField ? campo(block.header, def.sectionField) : undefined
            const prevSeccion = prev && def.sectionField
              ? campo(prev.header, def.sectionField)
              : undefined
            const bandaSeccion = seccion != null && seccion !== prevSeccion
            const chips = !open && block.details.length > 0
              ? def.blockSummary?.(comoDic(block.header), block.details.map(comoDic)) ?? []
              : []
            // `block.key` es `ordenBloque`, un ordinal de POSICIÓN de la vista, y
            // por tanto no vale como clave de una marca persistida: al renumerar
            // la vista, la marca reaparece sobre otra fila. La clave se compone de
            // los campos de identidad del registro — ver `marcasReport.ts`.
            const claveBloque = claveBloqueReport(def, block.header, block.key)
            const marcadaBloque = marcadas.has(claveBloque)

            return (
              <Fragment key={block.key}>
                {bandaSeccion && (
                  <tr>
                    <td
                      colSpan={totalCols}
                      className={`py-1.5 text-[11px] font-bold uppercase tracking-wider ${
                        dark ? 'bg-sky-500/10 text-sky-300' : 'bg-sky-50 text-sky-700'
                      }`}
                    >
                      {/* El `sticky` va en un HIJO, no en la celda. La celda
                          abarca la tabla entera con `colSpan`, así que fijarla no
                          mueve su contenido: el rótulo se quedaba en el extremo
                          izquierdo de esos 3.000 px y desaparecía al desplazar en
                          horizontal, dejando una franja de color sin texto —justo
                          cuando hace falta saber en qué sección estás, que es al
                          llegar a las columnas de la derecha—. En un hijo
                          `inline-block` sí se queda a la vista. Es el mismo
                          reparto que resolvió el color del desvío. */}
                      <span className="sticky left-0 inline-block px-3">
                        {sectionLabelUi(def, seccion, t)}
                      </span>
                    </td>
                  </tr>
                )}

                {/* ── Cabecera del bloque ──────────────────────────────────── */}
                <tr
                  className={`${marcadaBloque ? '' : headerHover} ${rowBorder} ${
                    marcadaBloque ? marcadoBg : headerBg
                  } ${block.details.length > 0 ? 'cursor-pointer' : ''}`}
                  onClick={block.details.length > 0 ? () => onToggle(block.key) : undefined}
                  aria-expanded={block.details.length > 0 ? open : undefined}
                >
                  {/* El expansor es un elemento propio con su área de clic: antes
                      salía pegado al valor y quien copiaba la celda se llevaba el
                      «+» dentro. */}
                  <td
                    className={`${tdCanaleta} ${marcadaBloque ? marcadoBg : headerBg} sticky left-0 z-10`}
                    style={{ width: GUTTER_W, minWidth: GUTTER_W }}
                  >
                    <span className="flex items-center gap-1">
                      <BotonMarca marcas={marcas} clave={claveBloque} dark={dark} marcada={marcadaBloque} />
                      {block.details.length > 0 ? (
                        <button
                          onClick={e => { e.stopPropagation(); onToggle(block.key) }}
                          className={`shrink-0 w-4 h-4 leading-none rounded font-bold cursor-pointer ${
                            dark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-200'
                          }`}
                          title={open ? t('reports.collapse') : t('reports.expand')}
                          aria-label={open ? t('reports.collapse') : t('reports.expand')}
                        >
                          {open ? '−' : '+'}
                        </button>
                      ) : (
                        <span className={`shrink-0 w-4 text-center ${muted}`}>·</span>
                      )}
                    </span>
                  </td>
                  {colsTabla.map((col, ci) => celda(
                    block.header, col, true, ci,
                    marcadaBloque ? marcadoBg : headerBg,
                    col.key === claveChips ? chips : undefined,
                  ))}
                </tr>

                {/* ── Detalle anidado: una subtabla por tipo ─────────────── */}
                {open && def.nestedDetail && def.detailTypes.map(tipo => {
                  // El índice ORIGINAL viaja con la fila: la clave de la marca se
                  // compone con él, y reiniciándolo en cada tipo el primer lote y
                  // el primer componente compartirían clave.
                  const filas = block.details
                    .map((row, di) => ({ row, di }))
                    .filter(x => String(x.row.tipoFila) === tipo)
                  const colsNivel = colsOfDetailType(cols, tipo)
                  if (filas.length === 0 || colsNivel.length === 0) return null

                  return (
                    <SubTableWrapper key={tipo} dark={dark} colSpan={totalCols}>
                      {/* La sangría izquierda alinea la subtabla con el expansor
                          del bloque, para que se lea como algo que cuelga de él. */}
                      <div className="pl-9 pr-3 py-2.5">
                        <div className={`mb-1.5 text-[10px] font-bold uppercase tracking-wider ${muted}`}>
                          {rotuloDetalle(tipo)}
                        </div>
                        {/* Ancho AUTOMÁTICO, no `w-full`: seis columnas estiradas
                            sobre los 19 de la maestra dejaban medio metro de hueco
                            entre el lote y su orden, y la subtabla se leía como
                            otra tabla en vez de como el desglose de la fila. */}
                        <table
                          className={`border-collapse text-left ${def.wrapHeaders ? 'table-fixed' : ''}`}
                          style={def.wrapHeaders
                            // La suma de sus columnas más la del marcador: con
                            // reparto fijo la tabla necesita un ancho del que
                            // partir. Lo que sobre hasta el ancho de la maestra se
                            // queda en blanco a la derecha, detrás de la última
                            // columna, que es donde no se interpone en la lectura.
                            ? { width: colsNivel.reduce((a, c) => a + (c.minW ?? 120), 26) }
                            : undefined}
                        >
                          <thead>
                            <tr className={`border-b ${subBorde}`}>
                              <CabeceraMarca />
                              {colsNivel.map(col => (
                                <th
                                  key={col.key}
                                  className={`${subTh} ${isNumericCol(col) ? 'text-right' : 'text-left'}`}
                                  style={ancho(col)}
                                >
                                  {col.tooltipKey ? (
                                    <span
                                      title={t(col.tooltipKey)}
                                      className="cursor-help border-b border-dotted border-current/40"
                                    >
                                      {colLabel(col, t)}
                                    </span>
                                  ) : colLabel(col, t)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filas.map(({ row, di }) => {
                              const especial = claseEspecial(row)
                              const limitante = Number(campo(row, 'esComponenteLimitante') ?? 0) === 1
                              const claveDetalle = claveDetalleReport(def, claveBloque, row, di)
                              const marcadaDetalle = marcadas.has(claveDetalle)
                              const casa = coincide?.(row) ?? false
                              // Mismo orden de precedencia que en la rejilla
                              // plana: la marca del usuario manda, después la
                              // coincidencia de la búsqueda y luego el ámbar del
                              // componente que frena.
                              const filaBg = marcadaDetalle
                                ? marcadoBg
                                : casa ? coincideBg : limitante ? limitanteBg : ''
                              return (
                                <tr
                                  key={di}
                                  className={`border-t ${subBorde} ${filaBg} ${filaBg ? '' : subHover} ${
                                    especial ? (dark ? 'italic text-slate-500' : 'italic text-slate-400') : ''
                                  }`}
                                  title={especial ? t(`reports.special.${especial}`) : undefined}
                                >
                                  <td className={`px-1 ${casa && !marcadaDetalle ? coincideBarra : ''}`}>
                                    <BotonMarca
                                      marcas={marcas}
                                      clave={claveDetalle}
                                      dark={dark}
                                      marcada={marcadaDetalle}
                                    />
                                  </td>
                                  {colsNivel.map(col => celdaSub(row, col))}
                                </tr>
                              )
                            })}
                          </tbody>
                          {/* Total de ESTE bloque y de este nivel. El pie de arriba
                              totaliza el conjunto filtrado, pero sobre la rejilla
                              maestra, donde estas columnas ya no están. */}
                          <tfoot>
                            <SubTotalsRow
                              dark={dark}
                              rows={filas.map(x => x.row)}
                              columnas={pieDeSubtabla(colsNivel)}
                            />
                          </tfoot>
                        </table>
                      </div>
                    </SubTableWrapper>
                  )
                })}

                {/* ── Detalle en la misma rejilla ────────────────────────── */}
                {open && !def.nestedDetail && block.details.map((row, di) => {
                  const especial = claseEspecial(row)
                  const limitante = Number(campo(row, 'esComponenteLimitante') ?? 0) === 1
                  // La clave de la sublínea empieza por la de su bloque: la
                  // línea 1 de un pedido no puede salir marcada en otro. Y lleva
                  // su `tipoFila`, para que una factura y una OP de la misma
                  // posición no compartan marca.
                  const claveDetalle = claveDetalleReport(def, claveBloque, row, di)
                  const marcadaDetalle = marcadas.has(claveDetalle)
                  // Es ESTA sublínea la que ha traído aquí al bloque.
                  const casa = coincide?.(row) ?? false
                  const filaBg = marcadaDetalle
                    ? marcadoBg
                    : casa ? coincideBg : limitante ? limitanteBg : detailBg
                  const filaHover = marcadaDetalle
                    ? ''
                    : casa ? coincideHover : limitante ? limitanteHover : detailHover

                  return (
                    <tr
                      key={`${block.key}-${di}`}
                      className={`${filaBg} ${filaHover} ${detailBorder} ${
                        // Gris y cursiva en las clases especiales, igual que el
                        // Excel que ya recibe el cliente.
                        especial ? (dark ? 'italic text-slate-500' : 'italic text-slate-400') : ''
                      }`}
                      title={especial ? t(`reports.special.${especial}`) : undefined}
                    >
                      {/* La barra de la coincidencia va en la canaleta y no en el
                          `<tr>`: la canaleta es la celda fija del extremo
                          izquierdo, así que la barra sigue viéndose con la tabla
                          desplazada, que es cuando más falta hace. */}
                      <td
                        className={`${tdCanaleta} ${filaBg} sticky left-0 z-10 ${muted} ${
                          casa && !marcadaDetalle ? coincideBarra : ''
                        }`}
                        style={{ width: GUTTER_W, minWidth: GUTTER_W }}
                      >
                        <span className="flex items-center gap-1">
                          <BotonMarca marcas={marcas} clave={claveDetalle} dark={dark} marcada={marcadaDetalle} />
                          <span>└</span>
                        </span>
                      </td>
                      {colsTabla.map((col, ci) => celda(row, col, false, ci, filaBg))}
                    </tr>
                  )
                })}
              </Fragment>
            )
          })}
        </tbody>

        <TableTotalsFooter
          dark={dark}
          columns={footerCols}
          // `results` y `rowCount` son el respaldo por si algún día no hubiera
          // grupos; hoy `footerGroups` trae siempre al menos uno.
          results={results}
          rowCount={aggRows.length}
          groups={footerGroups}
          stickyLabel={etiquetaPie}
        />
      </table>
      </div>
    </>
  )
}
